/**
 * WidgetViewElement — kreator formatek: klocek palety „Widget danych" (`widget_view`),
 * Faza 23 Etap 3 T2 — osadzenie widgetu danych z kreatora widgetów NA FORMULARZU kartoteki.
 *
 * Mirror `AdditionalTableViewElement` (wybór KTÓREJ definicji dotyczy klocek dzieje się w
 * `propertiesEditor`, real render na realnej stronie idzie przez osobny komponent z hookami —
 * tu `WidgetFormBlock`, bo `render`/`propertiesEditor` są wołane jak ZWYKŁE FUNKCJE, nie
 * komponenty, więc żaden hook nie może wejść bezpośrednio do ich ciała).
 *
 * `allowedModules: ['contractors', 'companies', 'employees']` — hosty MVP tego etapu (dokładnie
 * te trzy kartoteki mają dziś trasę detalu z `:id` czytanym przez `WidgetFormBlock`, patrz
 * `TenantTabPanels.tsx`). Rozszerzenie na kolejne kartoteki/klasy dokumentów to kolejny krok,
 * nie milcząca zmiana tej listy.
 *
 * Model „automatycznie z miejsca": picker filtruje definicje po ZDOLNOŚCI ICH ŹRÓDŁA do zaczepienia
 * o bieżącą kartotekę. Metadane źródeł (`/widget-definitions/sources`) niosą `contextEntities` —
 * encje-rodzice, na których formularzu źródło umie się związać (= mapa `contextKeys` rejestru).
 * Definicja pasuje, gdy `contextEntities` jej źródła zawiera `moduleSlug`. Samo wiązanie do rekordu
 * włącza potem formularz OBECNOŚCIĄ rekordu (silnik `compute.ts`), bez żadnej flagi w definicji —
 * ta sama definicja liczy zakres firmy na pulpicie, a na kartotece zawęża do otwartego rekordu.
 *
 * Bezpieczeństwo: widget dziedziczy widoczność listy źródła po stronie backendu — front tylko
 * przekazuje `recordId`/`entity` w query (decyzja architektoniczna, patrz `WidgetFormBlock.tsx`).
 */

import { useTranslation } from 'react-i18next';
import { Gauge } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import type {
  LayoutElementDefinition,
  LayoutElementPropertiesEditorProps,
} from '@/components/fields/LayoutElementRegistry';
import { useWidgetDefinitions, useWidgetSources } from '@/hooks/useWidgetDefinitions';
import i18n from '@/i18n';

import { WidgetFormBlock } from './WidgetFormBlock';

/** Kartoteki hostujące `widget_view` w tym etapie (Faza 23, Etap 3 T2). */
const WIDGET_VIEW_ALLOWED_MODULES = ['contractors', 'companies', 'employees'];

interface WidgetViewSettings {
  definitionId?: string | null;
  entity?: string | null;
}

function readSettings(raw: Record<string, unknown> | undefined): WidgetViewSettings {
  const s = raw ?? {};
  return {
    definitionId: typeof s.definitionId === 'string' ? s.definitionId : null,
    entity: typeof s.entity === 'string' ? s.entity : null,
  };
}

function WidgetViewPropertiesEditor({
  settings,
  onChange,
  moduleSlug,
}: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const definitionsQuery = useWidgetDefinitions();
  const sourcesQuery = useWidgetSources();
  const { definitionId } = readSettings(settings);

  // Definicja pasuje do kartoteki, gdy JEJ ŹRÓDŁO umie się o nią zaczepić (contextEntities z
  // metadanych źródeł). Wiązanie do rekordu włącza potem sam formularz obecnością rekordu.
  const contextBySource = new Map((sourcesQuery.data ?? []).map((s) => [s.key, s.contextEntities]));
  const compatible = (definitionsQuery.data ?? []).filter(
    (d) => d.isActive && (contextBySource.get(d.source) ?? []).includes(moduleSlug ?? ''),
  );

  const isPending = definitionsQuery.isPending || sourcesQuery.isPending;
  const isError = definitionsQuery.isError || sourcesQuery.isError;

  return (
    <div className="space-y-2">
      <span className="block text-xs text-text-secondary">
        {t('tenant.widgets.layoutElement.pickDefinition')}
      </span>
      {isPending ? (
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      ) : isError ? (
        <p className="text-sm text-danger-600">{t('tenant.widgets.errors.generic')}</p>
      ) : compatible.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          {t('tenant.widgets.layoutElement.noCompatible')}
        </p>
      ) : (
        <Dropdown
          value={definitionId ?? ''}
          onChange={(v) =>
            onChange({ definitionId: v || null, entity: v ? (moduleSlug ?? null) : null })
          }
          data-testid="widget-view-definition-selector"
          options={[
            { value: '', label: t('tenant.widgets.layoutElement.pickDefinition') },
            ...compatible.map((d) => ({ value: d.id, label: d.name })),
          ]}
        />
      )}
    </div>
  );
}

export const WidgetViewElement: LayoutElementDefinition = {
  type: 'widget_view',
  category: 'tresc',
  isContainer: false,
  label: i18n.t('tenant.widgets.layoutElement.label'),
  icon: 'Gauge',
  allowedModules: WIDGET_VIEW_ALLOWED_MODULES,
  defaultSettings: () => ({ definitionId: null, entity: null }),
  propertiesEditor: (props) => <WidgetViewPropertiesEditor {...props} />,
  render: ({ element, mode }) => {
    if (mode === 'builder') {
      return (
        <div
          className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
          data-testid={`widget-view-element-${element.id}`}
          data-element-type="widget_view"
        >
          <Gauge className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
          <p className="mt-1 text-xs font-medium text-text-secondary">
            {i18n.t('tenant.widgets.layoutElement.label')}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">
            {i18n.t('tenant.widgets.layoutElement.builderMock')}
          </p>
        </div>
      );
    }
    const { definitionId, entity } = readSettings(element.settings);
    return <WidgetFormBlock definitionId={definitionId ?? null} entity={entity ?? null} />;
  },
};

export default WidgetViewElement;
